// client/src/utils/apiClient.js

import axios from 'axios';

// Custom error class for handling API errors
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// State for token refresh management
let isRefreshing = false;
let refreshSubscribers = [];
let isAuthenticating = false;


export const isTokenExpiring = (token) => {
  if (!token) return true;  
  try {
    const decoded = JSON.parse(atob(token.split('.')[1]));
    // Add 5 minute buffer before actual expiration
    return (decoded.exp * 1000) - Date.now() <= 300000;  
  } catch (error) {
    console.error('Token decode error:', error);
    return true; 
  }
};

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

export const performLogout = async (setUser = null) => {
  try {
    await apiClient.post('/auth/logout');
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete apiClient.defaults.headers.common['Authorization'];
    
    if (setUser) {
      setUser(null);
    }
    
    window.location.href = '/login';
  }
};

export const refreshAuthToken = async () => {
  try {
    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh(token => resolve(token));
      });
    }

    isRefreshing = true;
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      throw new ApiError('No refresh token available', 401);
    }

    const response = await axios.post('/api/auth/refresh-token', { refreshToken });
    const { token, refreshToken: newRefreshToken } = response.data.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    onTokenRefreshed(token);
    isRefreshing = false;
    
    return token;
  } catch (error) {
    isRefreshing = false;
    refreshSubscribers = [];
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      throw new ApiError('Session expired', 401);
    }
    throw error;
  }
};

apiClient.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (!token) return config;

    // Check if token is about to expire
    if (isTokenExpiring(token)) {
      try {
        const newToken = await refreshAuthToken();
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (error) {
        // If refresh fails, continue with current token
        // The response interceptor will handle any 401s
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  }
);

// Updated response interceptor with better error handling
apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints except refresh-token
      if (originalRequest.url.includes('/auth/') && 
          !originalRequest.url.includes('/auth/refresh-token')) {
        throw error;
      }

      try {
        originalRequest._retry = true;
        const token = await refreshAuthToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Only redirect to login if we're not already there
        if (!window.location.pathname.includes('login')) {
          window.location.href = '/login';
        }
        throw refreshError;
      }
    }
    throw error;
  }
);

// Expose API functions for different routes
export const api = {
  admin: {
    getOverview: () => apiClient.get('/admin/overview'),
    deleteModel: (endpoint) => apiClient.post(`/admin/delete/${endpoint}`),
    getData: () => apiClient.get('/admin/overview'),
    getActivity: () => apiClient.get('/admin/activity'),
  },
  
  auth: {
    login: (credentials) => apiClient.post('/auth/login', credentials),
    register: (userData) => apiClient.post('/auth/register', userData),
    logout: () => performLogout(),
    me: () => apiClient.get('/auth/me'),
  },

  workOrders: {
    getAll: () => apiClient.get('/work-orders'),
    generate: () => apiClient.post('/work-orders/generate'),
    getByPart: (partId, includeCompleted = false) =>
      apiClient.get('/work-orders', { 
        params: { 
          partId, 
          includeCompleted 
        }
      }),
    updateStatus: (orderId, status) =>
      apiClient.put(`/work-orders/${orderId}/status`, { status }),
    getScheduleImpact: (partId) =>
      apiClient.get(`/work-orders/schedule-impact/${partId}`)
  },

  sixS: {
    getAreas: () => apiClient.get('/sixs/areas'),
    getAreaDetails: (id) => apiClient.get(`/sixs/areas/${id}`),
    createArea: (data) => apiClient.post('/sixs/areas', data),
    deleteArea: (id) => apiClient.delete(`/sixs/areas/${id}`),
    submitAudit: (data) => apiClient.post('/sixs/submit', {
      areaId: data.areaId,
      questions: data.questions.map(q => ({
        questionId: q.questionId,
        score: Number(q.score),
        comments: q.comments || '',
        category: q.category
      })),
      recommendations: data.recommendations || ''
    }),
    getHistory: (areaId, params) => 
      apiClient.get(`/sixs/history/${areaId}`, { params }),
    getDashboard: () => apiClient.get('/sixs/dashboard')
  },

  analytics: {
    // Get dashboard analytics data
    getDashboard: (params = {}) => 
      apiClient.get('/analytics/dashboard', { 
        params: {
          dateRange: params.dateRange,
          shift: params.shift,
          machineId: params.machineId
        }
      }),
  
    // Get specific metric data
    getMetrics: (metricType, params = {}) =>
      apiClient.get(`/analytics/${metricType}`, { params }),
  
    // Get machine performance data
    getMachinePerformance: (machineId, params = {}) =>
      apiClient.get(`/analytics/machines/${machineId}/performance`, { params }),
  
    // Get audit performance trends
    getAuditTrends: (params = {}) =>
      apiClient.get('/analytics/audits/trends', { params }),
  
    // Get work order analytics
    getWorkOrderMetrics: (params = {}) =>
      apiClient.get('/analytics/work-orders/metrics', { params }),
  
    // Get inventory analytics
    getInventoryMetrics: (params = {}) =>
      apiClient.get('/analytics/inventory/metrics', { params }),
  
    // Export analytics data
    exportData: (format, filters = {}) =>
      apiClient.post('/analytics/export', {
        format,
        filters
      }, {
        responseType: format === 'csv' || format === 'xlsx' ? 'blob' : 'json'
      })
  },

  kaizen: {
    create: (data) => apiClient.post('/kaizen', data),
    getAll: (params) => apiClient.get('/kaizen', { params }),
    vote: (id, data) => apiClient.post(`/kaizen/${id}/vote`, data),
    getTopContributors: () => apiClient.get('/kaizen/top-contributors'),
    getSingle: (id) => apiClient.get(`/kaizen/${id}`),
    delete: (id) => apiClient.delete(`/kaizen/${id}`),
    updateStatus: (id, data) => apiClient.put(`/kaizen/${id}/status`, data),
  },

  machines: {
    getAll: (options = {}) => apiClient.get('/machines', {
      params: {
        ...options,
        include: options.include || 'currentMold.associatedParts'
      }
    }),
    create: (data) => apiClient.post('/machines', data),
    delete: (machineName) => apiClient.delete(`/machines/${encodeURIComponent(machineName)}`),
    getIssues: (machineIds) => apiClient.get('/machines/issues', { params: { machineIds } }),
    createIssue: (machineId, data) => apiClient.post(`/machines/${machineId}/issues`, data),
    updateIssueStatus: (machineId, issueId, data) => 
      apiClient.put(`/machines/${machineId}/issues/${issueId}`, data),
    updateMold: (machineId, moldId) => 
      apiClient.post(`/machines/${machineId}/mold/${moldId}`),
    getCurrentMold: (machineId) => 
      apiClient.get(`/machines/${machineId}/current-mold`),
    getSchedule: (machineId, startDate, endDate) =>
      apiClient.get(`/machines/${machineId}/schedule`, {params: { startDate, endDate }}),
    changeMold: (machineId, moldId) =>
      apiClient.post(`/machines/${machineId}/mold/${moldId}`),
    getPriority: (machineId) => 
      apiClient.get(`/machines/priority/${machineId}`)
  },

  molds: {
    getAll: () => apiClient.get('/molds'),
    create: (data) => apiClient.post('/molds', data),
    delete: (id) => apiClient.delete(`/molds/${id}`),
    getCurrent: (machineId) => apiClient.get(`/molds/current/${machineId}`),
    updatePartCavities: (moldId, partCavities) =>
      apiClient.put(`/molds/${moldId}/part-cavities`, { partCavities }),
    getCompatibility: (moldId) =>
      apiClient.get(`/molds/${moldId}/compatibility`),
    getProductionRate: (moldId, partId) =>
      apiClient.get(`/molds/${moldId}/production-rate/${partId}`)
  },

  moldSchedule: {
    getCurrent: () => apiClient.get('/mold-schedule/current'),
    generate: (params = {}) => apiClient.post('/mold-schedule/generate', params),
    validateChange: (machineId, moldId, scheduledTime) => 
      apiClient.post('/mold-schedule/validate-change', {
        machineId,
        moldId,
        scheduledTime
      }),
    scheduleChange: (machineId, moldId, scheduledTime) =>
      apiClient.post('/mold-schedule/schedule-change', {
        machineId,
        moldId,
        scheduledTime
      }),
    getAvailableSlots: (machineId, date) =>
      apiClient.get(`/mold-schedule/available-slots/${machineId}`, {
        params: { date }
      }),
    getScheduleReport: (startDate, endDate) =>
      apiClient.get('/mold-schedule/report', {
        params: { startDate, endDate }
      })
  },

  safety: {
    // Safety Areas
    getAreas: (params = {}) => 
      apiClient.get('/safety/areas', { params }),
    
    getAreaDetails: (id) => 
      apiClient.get(`/safety/areas/${id}`),
    
    createArea: (data) => 
      apiClient.post('/safety/areas', data),
    
    updateArea: (id, data) => 
      apiClient.put(`/safety/areas/${id}`, data),
    
    deleteArea: (id) => 
      apiClient.delete(`/safety/areas/${id}`),

    // Safety Audits
    getAudits: (params = {}) => 
      apiClient.get('/safety/audits', { params }),
    
    submitAudit: (data) => 
      apiClient.post('/safety/audits', data),
    
    getAuditDetails: (id) => 
      apiClient.get(`/safety/audits/${id}`),
    
    updateAudit: (id, data) => 
      apiClient.put(`/safety/audits/${id}`, data),

    // Safety Inspections
    getInspections: (params = {}) => 
      apiClient.get('/safety/inspections', { params }),
    
    submitInspection: (data) => 
      apiClient.post('/safety/inspections', data),
    
    getInspectionDetails: (id) => 
      apiClient.get(`/safety/inspections/${id}`),
    
    updateInspection: (id, data) => 
      apiClient.put(`/safety/inspections/${id}`, data),
    
    verifyInspection: (id) => 
      apiClient.post(`/safety/inspections/${id}/verify`),

    // Safety Incidents
    getIncidents: (params = {}) => 
      apiClient.get('/safety/incidents', { params }),
    
    reportIncident: (data) => 
      apiClient.post('/safety/incidents/report', data),
    
    getIncidentDetails: (id) => 
      apiClient.get(`/safety/incidents/${id}`),
    
    updateIncident: (id, data) => 
      apiClient.put(`/safety/incidents/${id}`, data),
    
    addInvestigation: (id, data) => 
      apiClient.post(`/safety/incidents/${id}/investigation`, data),
    
    addCorrectiveAction: (id, data) => 
      apiClient.post(`/safety/incidents/${id}/actions`, data),
    
    updateCorrectiveAction: (incidentId, actionId, data) => 
      apiClient.put(`/safety/incidents/${incidentId}/actions/${actionId}`, data),
    
    closeIncident: (id) => 
      apiClient.post(`/safety/incidents/${id}/close`),

    // Area-specific data
    getAreaIncidents: (areaId, params = {}) => 
      apiClient.get(`/safety/areas/${areaId}/incidents`, { params }),

    // Safety Analytics & Dashboard
    getDashboard: (params = {}) => 
      apiClient.get('/safety/dashboard', { params }),
    
    getAnalytics: (params = {}) => 
      apiClient.get('/safety/analytics', { params })
  },

  preventiveMaintenance: {
    // Get dashboard data
    getDashboard: (params = {}) => 
      apiClient.get('/pm/dashboard', { params }),
    
    // PM Area operations
    getAreas: (params = {}) => 
      apiClient.get('/pm/areas', { params }),
    
    getAreaDetails: (id) => 
      apiClient.get(`/pm/areas/${id}`),
    
    createArea: (data) => 
      apiClient.post('/pm/areas', data),
    
    updateArea: (id, data) => 
      apiClient.put(`/pm/areas/${id}`, data),
    
    deleteArea: (id) => 
      apiClient.delete(`/pm/areas/${id}`),
    
    // PM Task operations
    submitCompletion: (data) => {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      return apiClient.post('/pm/completions', data, config);
    },
    
    // Get completion records
    getCompletions: (params = {}) => 
      apiClient.get('/pm/completions', { params }),
    
    getCompletionDetails: (id) => 
      apiClient.get(`/pm/completions/${id}`),
    
    // PM Schedule operations
    getSchedule: (params = {}) => 
      apiClient.get('/pm/schedule', { params }),
    
    // Analytics
    getAnalytics: (params = {}) => 
      apiClient.get('/pm/analytics', { params }),
    
    // Export
    exportAnalytics: (params = {}, options = {}) => 
      apiClient.get('/pm/analytics/export', { params, ...options }),

    getCommonDescriptions: () =>
      apiClient.get('/pm/common/descriptions'),
      
    getCommonTaskDescriptions: () =>
      apiClient.get('/pm/common/task-descriptions'),
      
    getCommonTools: () =>
      apiClient.get('/pm/common/tools'),
      
    getCustomCategories: () =>
      apiClient.get('/pm/common/categories'),
      
    addCustomCategory: (category) =>
      apiClient.post('/pm/common/categories', { category }),
      
    searchSpareParts: (query) =>
      apiClient.get('/pm/spare-parts/search', { params: { query } }),

    getAreaSchedules: (areaId) => apiClient.get(`/pm/areas/${areaId}/schedules`),

  },

  parts: {
    getAll: () => apiClient.get('/parts'),
    create: (data) => apiClient.post('/parts', data),
    delete: (id) => apiClient.delete(`/parts/${id}`),
    updateInventory: (partId, quantity, isAddition = true) =>
      apiClient.put(`/parts/${partId}/inventory`, { quantity, isAddition }),
    getInventoryStatus: (partId) =>
      apiClient.get(`/parts/${partId}/inventory-status`)
  },

  users: {
    // Updated to support query parameters for search, filtering, and sorting
    getAll: (params = {}) => {
      // Build query string from parameters
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters if provided
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add search parameter if provided
      if (params.search) queryParams.append('search', params.search);
      
      // Add role filter if provided
      if (params.role) queryParams.append('role', params.role);
      
      // Add sorting parameters if provided
      if (params.sort) queryParams.append('sort', params.sort);
      if (params.order) queryParams.append('order', params.order);
      
      // Convert query params to string
      const queryString = queryParams.toString();
      
      // Make the API call
      return apiClient.get(`/users${queryString ? `?${queryString}` : ''}`);
    },
    delete: (id) => apiClient.delete(`/users/${id}`),
  },

  ai: {
    chat: (message) => apiClient.post('/ai/chat', { message }),
  },

  supervisor: {
    getData: () => apiClient.get('/supervisor'),
  },

  user: {
    getData: () => apiClient.get('/user'),
  },

  // Generic methods
  get: (endpoint) => apiClient.get(endpoint),
  post: (endpoint, data) => apiClient.post(endpoint, data),
  put: (endpoint, data) => apiClient.put(endpoint, data),
  delete: (endpoint) => apiClient.delete(endpoint),
};

export const setupAuthToken = () => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 > Date.now()) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return true;
      }
    } catch (error) {
      console.error('Token validation error:', error);
    }
    // Invalid or expired token
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }
  return false;
};

export default apiClient;